<template>
  <div class="min-h-screen">

    <!-- Page Header -->
    <div class="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl
                    flex items-center justify-center shadow-lg shadow-orange-500/30">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p class="text-sm text-slate-500 mt-0.5">Overview of your customer reviews</p>
        </div>
      </div>

      <router-link
          :to="{ name: 'app.reviews' }"
          class="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600
                 hover:from-orange-600 hover:to-orange-700 text-white text-sm font-semibold rounded-xl
                 shadow-md shadow-orange-500/25 transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/30
                 hover:-translate-y-0.5"
      >
        View All Reviews
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
      </router-link>
    </div>

    <!-- Stat cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
      <div v-for="card in cards" :key="card.label"
           class="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 transition-all duration-200
                  hover:shadow-md hover:-translate-y-0.5">
        <div class="flex items-start justify-between">
          <div>
            <p class="text-xs font-semibold text-slate-500 uppercase tracking-wider">{{ card.label }}</p>

            <div v-if="loading" class="h-9 w-20 bg-slate-200 rounded-lg animate-pulse mt-2"></div>
            <p v-else class="text-3xl font-bold text-slate-900 mt-2">{{ card.value }}</p>

            <div v-if="card.label === 'Average Rating' && !loading" class="flex items-center gap-0.5 mt-1.5">
              <span v-for="s in 5" :key="s" class="text-sm"
                    :class="s <= Math.round(dashboard.average_rate) ? 'text-orange-500' : 'text-slate-200'">★</span>
            </div>
            <p v-else-if="!loading" class="text-xs text-slate-400 mt-1.5">{{ card.hint }}</p>
          </div>

          <div class="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" :class="card.iconBg">
            <span class="text-lg" :class="card.iconColor" v-html="card.icon"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Latest reviews -->
    <div class="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 class="text-base font-bold text-slate-900">Latest Reviews</h2>
          <p class="text-xs text-slate-400 mt-0.5">The 10 most recent submissions</p>
        </div>
        <span class="text-xs text-slate-400">{{ dashboard.latest_reviews.length }} shown</span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
          <tr class="bg-slate-50 border-b border-slate-100">
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">ID</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Rating</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
            <th class="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Received</th>
            <th class="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">View</th>
          </tr>
          </thead>

          <!-- Skeleton loading -->
          <tbody v-if="loading">
          <tr v-for="i in 5" :key="i" class="border-b border-slate-50">
            <td class="px-5 py-4"><div class="h-4 w-8 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4"><div class="h-4 w-32 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4"><div class="h-4 w-20 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4"><div class="h-4 w-36 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4 text-center"><div class="h-6 w-16 bg-slate-200 rounded-full animate-pulse mx-auto"></div></td>
            <td class="px-5 py-4"><div class="h-4 w-24 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4 text-center"><div class="h-8 w-10 bg-slate-200 rounded-xl animate-pulse mx-auto"></div></td>
          </tr>
          </tbody>

          <!-- Empty state -->
          <tbody v-else-if="dashboard.latest_reviews.length === 0">
          <tr>
            <td colspan="7" class="px-5 py-20">
              <div class="text-center">
                <div class="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg class="w-8 h-8 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                  </svg>
                </div>
                <p class="font-semibold text-slate-600">No reviews yet</p>
                <p class="text-sm text-slate-400 mt-1">When customers leave reviews, they'll appear here.</p>
              </div>
            </td>
          </tr>
          </tbody>

          <!-- Data rows -->
          <tbody v-else class="divide-y divide-slate-50">
          <tr v-for="review in dashboard.latest_reviews" :key="review.id"
              class="hover:bg-orange-50/40 transition-colors duration-150 group">

            <td class="px-5 py-4">
                <span class="font-mono text-xs font-bold text-orange-700 bg-orange-50 px-2 py-1 rounded-lg tracking-wider">
                  #{{ review.id }}
                </span>
            </td>

            <td class="px-5 py-4">
              <div class="flex items-center gap-2.5">
                <div class="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center
                            justify-center text-white text-xs font-bold flex-shrink-0">
                  {{ review.full_name?.slice(0, 2).toUpperCase() }}
                </div>
                <span class="text-sm font-semibold text-slate-800 max-w-[160px] truncate">{{ review.full_name }}</span>
              </div>
            </td>

            <td class="px-5 py-4">
              <div class="flex items-center gap-1">
                <span v-for="s in 5" :key="s" class="text-sm"
                      :class="s <= review.rate ? 'text-orange-500' : 'text-slate-200'">★</span>
                <span class="ms-1 text-xs font-semibold text-slate-500">{{ review.rate }}/5</span>
              </div>
            </td>

            <td class="px-5 py-4">
              <span class="text-sm text-slate-700 font-medium max-w-[200px] truncate block">{{ review.title }}</span>
            </td>

            <td class="px-5 py-4 text-center">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                      :class="review.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'">
                  <span class="w-1.5 h-1.5 rounded-full" :class="review.active ? 'bg-emerald-500' : 'bg-red-500'"></span>
                  {{ review.active ? 'Active' : 'Inactive' }}
                </span>
            </td>

            <td class="px-5 py-4">
              <span class="text-xs text-slate-500">{{ dayjs(review.created_at).fromNow() }}</span>
            </td>

            <td class="px-5 py-4 text-center">
              <RouterLink :to="{ name: 'app.reviews.show', params: { id: review.id } }"
                          class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold
                                 bg-slate-50 text-slate-700 border border-slate-200
                                 hover:bg-slate-100 hover:border-slate-300
                                 rounded-xl transition-all duration-200"
                          title="View">
                <EyeIcon class="w-3.5 h-3.5"/>
              </RouterLink>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted} from 'vue'
import {RouterLink} from 'vue-router'
import {EyeIcon} from '@heroicons/vue/24/outline'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import store from '@/store'

dayjs.extend(relativeTime)

const dashboard = computed(() => store.state.dashboard)
const loading = computed(() => store.state.dashboard.loading)

const cards = computed(() => [
  {
    label: 'Total Reviews',
    value: dashboard.value.total_reviews,
    hint: 'All reviews received',
    icon: '★',
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-500',
  },
  {
    label: 'Active',
    value: dashboard.value.active_reviews,
    hint: 'Visible on the site',
    icon: '●',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
  },
  {
    label: 'Inactive',
    value: dashboard.value.inactive_reviews,
    hint: 'Awaiting approval',
    icon: '●',
    iconBg: 'bg-red-50',
    iconColor: 'text-red-500',
  },
  {
    label: 'Average Rating',
    value: Number(dashboard.value.average_rate ?? 0).toFixed(2),
    hint: 'Across all reviews',
    icon: '☆',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
  },
])

onMounted(() => {
  store.dispatch('getDashboard')
})
</script>
